import {
  Extension,
  PasteRule,
  combineTransactionSteps,
  findChildrenInRange,
  getChangedRanges,
} from "@tiptap/core";
import type { MarkType } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { Transaction } from "@tiptap/pm/state";
import { isValidPhoneValue, normalizePhoneNumber } from "@salestrak/custom-fields";

/**
 * Phone numbers typed or pasted into rich text become `tel:` links on the
 * editor's existing `link` mark — the same treatment StarterKit's Link gives
 * a URL or an email, so a number in a comment is tappable in every browser
 * rather than only where a browser's own data detector happens to run (the
 * app turns those off; see index.html).
 *
 * The gate is the custom-field engine's phone parser (`isValidPhoneValue`,
 * US default region, the same predicate the phone properties validate with;
 * `normalizePhoneNumber` then yields the E.164 form). It rejects the lookalikes a
 * regex alone would link — dates, seven-digit locals, order numbers — and the
 * href carries the E.164 form, so `(208) 555-0123`, `208.555.0123` and
 * `+1 208 555 0123` all dial the same number.
 *
 * A number contains spaces, so unlike Link's autolink this cannot read "the
 * last word before the space". It scans the trailing run of phone characters
 * in the block and tries every suffix that starts on a group boundary,
 * longest first: "Order 12 (208) 555-0123" links the number, not the order.
 */

/** Characters a phone number is written with. One trailing run of these,
 *  ending in a digit, is the candidate the parser judges. */
const PHONE_RUN_AT_END = /[+(]?\d[\d\s().-]*\d$/;
/** Same run, anywhere — the paste rule's finder. Bounded below by the
 *  shortest valid number (7 digits + separators) so a stray "12" is never
 *  handed to the parser. */
const PHONE_RUN_ANYWHERE = /[+(]?\d[\d\s().-]{5,}\d/g;
/** The longest run worth parsing: E.164 caps at 15 digits, and a written
 *  number carries at most a handful of separators around them. */
const MAX_RUN_LENGTH = 24;

const UNICODE_WHITESPACE_REGEX_END = /[\0- \xA0\u1680\u180E\u2000-\u2029\u205F\u3000]$/;

export type PhoneMatch = { start: number; end: number; href: string };

/** The longest suffix of `text`'s trailing phone-character run that the
 *  parser accepts, as offsets into `text` plus its `tel:` href. Null when the
 *  text does not end in a dialable number. */
export function findPhoneAtEnd(text: string): PhoneMatch | null {
  const run = PHONE_RUN_AT_END.exec(text);
  if (!run) return null;
  const runStart = run.index + Math.max(0, run[0].length - MAX_RUN_LENGTH);
  return longestValidSuffix(text, runStart, text.length);
}

/** Try each suffix of `text.slice(start, end)` that begins on a group
 *  boundary — the run itself, then after each separator — and return the
 *  first (longest) the parser accepts. */
function longestValidSuffix(
  text: string,
  start: number,
  end: number,
): PhoneMatch | null {
  let from = start;
  while (from < end) {
    const candidate = text.slice(from, end);
    if (isValidPhoneValue(candidate)) {
      return { start: from, end, href: `tel:${normalizePhoneNumber(candidate)}` };
    }
    // Advance to the next group boundary so the next candidate never starts
    // mid-number. Closing separators are skipped; an opening paren is kept,
    // since "(208) 555-0123" is the number and "208) 555-0123" is not.
    const next = text.slice(from + 1, end).search(/[\s().-]/);
    if (next === -1) break;
    from = from + 1 + next;
    while (from < end && /[\s).-]/.test(text[from] ?? "")) from += 1;
  }
  return null;
}

const phoneAutolinkKey = new PluginKey("phoneAutolink");

/** The as-you-type half: when a change leaves whitespace at the end of what
 *  was typed (a space, or Enter splitting the block), the text before it is
 *  checked for a trailing number. Mirrors Link's autolink so the two never
 *  disagree about WHEN a link lands. */
function phoneAutolink(type: MarkType): Plugin {
  return new Plugin({
    key: phoneAutolinkKey,
    appendTransaction: (transactions, oldState, newState) => {
      const docChanged =
        transactions.some((transaction) => transaction.docChanged) &&
        !oldState.doc.eq(newState.doc);
      const prevented = transactions.some((transaction) =>
        transaction.getMeta("preventAutolink"),
      );
      if (!docChanged || prevented) return;

      const { tr } = newState;
      const transform = combineTransactionSteps(oldState.doc, [...transactions]);
      const changes = getChangedRanges(transform);

      changes.forEach(({ newRange }) => {
        const textBlocks = findChildrenInRange(newState.doc, newRange, (node) =>
          node.isTextblock,
        );
        const textBlock = textBlocks[0];
        if (!textBlock) return;
        let textBefore: string;

        if (textBlocks.length > 1) {
          // Enter split the block: the first block's whole text is complete.
          textBefore = newState.doc.textBetween(
            textBlock.pos,
            textBlock.pos + textBlock.node.nodeSize,
            undefined,
            " ",
          );
        } else {
          const endText = newState.doc.textBetween(newRange.from, newRange.to, " ", " ");
          if (!UNICODE_WHITESPACE_REGEX_END.test(endText)) return;
          textBefore = newState.doc.textBetween(textBlock.pos, newRange.to, undefined, " ");
        }

        const match = findPhoneAtEnd(textBefore.trimEnd());
        if (!match) return;
        // `textBetween(block.pos, …)` starts at the block's opening token, so
        // text offset 0 sits at `pos + 1`.
        const from = textBlock.pos + 1 + match.start;
        const to = textBlock.pos + 1 + match.end;
        markPhone(tr, type, from, to, match.href);
      });

      return tr.steps.length ? tr : undefined;
    },
  });
}

/** Apply the link unless the range already carries one, or sits in code. */
function markPhone(
  tr: Transaction,
  type: MarkType,
  from: number,
  to: number,
  href: string,
): void {
  const { doc } = tr;
  const codeType = doc.type.schema.marks.code;
  if (codeType && doc.rangeHasMark(from, to, codeType)) return;
  if (doc.rangeHasMark(from, to, type)) return;
  tr.addMark(from, to, type.create({ href }));
}

/** The paste half: every phone run in pasted text that the parser accepts
 *  becomes a link, on the sub-range that parsed. */
function phonePasteRule(type: MarkType): PasteRule {
  return new PasteRule({
    find: PHONE_RUN_ANYWHERE,
    handler: ({ state, range, match }) => {
      const text = match[0];
      const found = longestValidSuffix(text, 0, text.length);
      // A run the parser rejects is simply not a phone number. Returning null
      // here would tell Tiptap the RULE failed, and it then drops every mark
      // this paste produced — including the numbers that did parse.
      if (!found) return;
      markPhone(
        state.tr,
        type,
        range.from + found.start,
        range.from + found.end,
        found.href,
      );
    },
  });
}

export const PhoneLink = Extension.create({
  name: "phoneLink",

  addProseMirrorPlugins() {
    const type = this.editor.schema.marks.link;
    return type ? [phoneAutolink(type)] : [];
  },

  addPasteRules() {
    const type = this.editor.schema.marks.link;
    return type ? [phonePasteRule(type)] : [];
  },
});
